import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { superApi } from "@/lib/superApi";
import { fmtCurrency } from "@/lib/utils-app";
import { Calendar, CreditCard, Landmark, Loader2, Check, X } from "lucide-react";

const STATUS_LABELS = {
  pending: "Pendiente",
  paid: "Pagado",
  rejected: "Rechazado",
};

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function SuperBilling() {
  const [platform, setPlatform] = useState(null);
  const [payments, setPayments] = useState([]);
  const [trialStart, setTrialStart] = useState("");
  const [trialEnd, setTrialEnd] = useState("");
  const [billingInterval, setBillingInterval] = useState("monthly");
  const [periodStart, setPeriodStart] = useState("");
  const [monthlyAmount, setMonthlyAmount] = useState("0");
  const [annualAmount, setAnnualAmount] = useState("0");
  const [mpPublicKey, setMpPublicKey] = useState("");
  const [mpAccessToken, setMpAccessToken] = useState("");
  const [transferInstructions, setTransferInstructions] = useState("");
  const [warningDays, setWarningDays] = useState("3");
  const [saving, setSaving] = useState("");

  const load = async () => {
    const [platRes, payRes] = await Promise.all([
      superApi.get("/super/platform"),
      superApi.get("/super/platform/subscription/payments"),
    ]);
    const p = platRes.data;
    setPlatform(p);
    setPayments(payRes.data || []);
    setTrialStart(p.trial_start_date || "");
    setTrialEnd(p.trial_end_date || "");
    setBillingInterval(p.billing_interval || "monthly");
    setMonthlyAmount(String(p.monthly_amount ?? 0));
    setAnnualAmount(String(p.annual_amount ?? 0));
    setMpPublicKey(p.platform_mp_public_key || "");
    setMpAccessToken(p.platform_mp_access_token || "");
    setTransferInstructions(p.transfer_instructions || "");
    setWarningDays(String(p.warning_days ?? 3));
    const started = (p.subscription_started_at || "").slice(0, 10);
    setPeriodStart(started || todayIso());
  };

  useEffect(() => {
    load().catch(() => toast.error("No se pudo cargar suscripción"));
  }, []);

  const saveTrial = async () => {
    setSaving("trial");
    try {
      const { data } = await superApi.put("/super/platform/subscription/trial", {
        trial_start_date: trialStart,
        trial_end_date: trialEnd,
      });
      setPlatform(data);
      toast.success("Periodo de prueba actualizado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar fechas");
    } finally {
      setSaving("");
    }
  };

  const activateSubscription = async () => {
    setSaving("activate");
    try {
      const { data } = await superApi.put("/super/platform/subscription/activate", {
        billing_interval: billingInterval,
        monthly_amount: parseFloat(monthlyAmount) || 0,
        annual_amount: parseFloat(annualAmount) || 0,
        period_start_date: periodStart || undefined,
      });
      setPlatform(data);
      toast.success("Suscripción activada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al activar");
    } finally {
      setSaving("");
    }
  };

  const suspendSubscription = async () => {
    setSaving("suspend");
    try {
      const { data } = await superApi.put("/super/platform/subscription/suspend");
      setPlatform(data);
      toast.success("Suscripción suspendida");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al suspender");
    } finally {
      setSaving("");
    }
  };

  const reactivateSubscription = async () => {
    setSaving("reactivate");
    try {
      const { data } = await superApi.put("/super/platform/subscription/reactivate");
      setPlatform(data);
      toast.success("Suscripción reactivada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al reactivar");
    } finally {
      setSaving("");
    }
  };

  const saveBillingConfig = async () => {
    setSaving("billing");
    try {
      const wd = Math.max(1, Math.min(30, parseInt(warningDays, 10) || 3));
      await superApi.put("/super/platform/billing-config", {
        platform_mp_public_key: mpPublicKey,
        platform_mp_access_token: mpAccessToken,
        transfer_instructions: transferInstructions,
        warning_days: wd,
      });
      toast.success("Configuración de cobro guardada");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar cobro");
    } finally {
      setSaving("");
    }
  };

  const confirmPayment = async (id) => {
    try {
      await superApi.put(`/super/platform/subscription/payments/${id}/confirm`);
      toast.success("Transferencia confirmada");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al confirmar");
    }
  };

  const rejectPayment = async (id) => {
    try {
      await superApi.put(`/super/platform/subscription/payments/${id}/reject`);
      toast.success("Transferencia rechazada");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al rechazar");
    }
  };

  if (!platform) {
    return <div className="text-white/60">Cargando…</div>;
  }

  const phaseLabel = {
    trial: "Periodo de prueba",
    active: "Suscripción activa",
    suspended: "Suspendida",
  }[platform.subscription_phase] || platform.subscription_phase;

  const isSuspended = platform.subscription_phase === "suspended";

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#E63946]">DanySolutions</div>
        <h1 className="font-display text-4xl text-white mt-1">Suscripción y cobros</h1>
        <p className="text-white/60 text-sm mt-2">
          Periodo de prueba, activación, Mercado Pago de plataforma y transferencias.
        </p>
      </div>

      <div className="glass p-5 rounded-sm space-y-4">
        <div className="text-sm text-white/70">
          Estado: <span className="text-white font-medium">{phaseLabel}</span>
          {platform.current_period_end && (
            <span className="ml-3">· Vence: {platform.current_period_end}</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {isSuspended ? (
            <button
              type="button"
              onClick={reactivateSubscription}
              disabled={saving === "reactivate"}
              className="btn-primary text-sm"
            >
              {saving === "reactivate" ? <Loader2 className="animate-spin" size={14} /> : null}
              Reactivar suscripción
            </button>
          ) : (
            <button
              type="button"
              onClick={suspendSubscription}
              disabled={saving === "suspend" || platform.subscription_phase === "trial"}
              className="btn-outline text-sm"
              title={
                platform.subscription_phase === "trial"
                  ? "Solo aplica con suscripción activa"
                  : undefined
              }
            >
              {saving === "suspend" ? <Loader2 className="animate-spin" size={14} /> : null}
              Suspender suscripción
            </button>
          )}
        </div>
        <p className="text-xs text-white/40">
          Suspender bloquea el panel admin por fase de suscripción. No borra suscriptores ni datos del gym.
          Reactivar vuelve a fase activa sin cambiar la fecha de vencimiento.
        </p>
      </div>

      <section className="glass p-5 rounded-sm space-y-4">
        <div className="flex items-center gap-2 text-white">
          <Calendar size={18} className="text-[#FFD700]" />
          <h2 className="font-display text-xl">Periodo de prueba</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block text-xs text-white/60">
            Inicio
            <input type="date" className="input-dark w-full mt-1" value={trialStart} onChange={(e) => setTrialStart(e.target.value)} />
          </label>
          <label className="block text-xs text-white/60">
            Fin
            <input type="date" className="input-dark w-full mt-1" value={trialEnd} onChange={(e) => setTrialEnd(e.target.value)} />
          </label>
        </div>
        <button type="button" onClick={saveTrial} disabled={saving === "trial"} className="btn-primary text-sm">
          {saving === "trial" ? <Loader2 className="animate-spin" size={14} /> : null}
          Guardar fechas de prueba
        </button>
      </section>

      <section className="glass p-5 rounded-sm space-y-4">
        <h2 className="font-display text-xl text-white">Activar suscripción</h2>
        <p className="text-xs text-white/50">
          Marca que el cliente pasó de prueba a suscripción pagada. Define intervalo, fecha de inicio del periodo y montos en MXN.
        </p>
        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2 text-white/80">
            <input type="radio" checked={billingInterval === "monthly"} onChange={() => setBillingInterval("monthly")} />
            Mensual
          </label>
          <label className="flex items-center gap-2 text-white/80">
            <input type="radio" checked={billingInterval === "annual"} onChange={() => setBillingInterval("annual")} />
            Anual
          </label>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <label className="block text-xs text-white/60">
            Inicio del periodo
            <input
              type="date"
              className="input-dark w-full mt-1"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
            />
          </label>
          <label className="block text-xs text-white/60">
            Monto mensual (MXN)
            <input type="number" min="0" step="0.01" className="input-dark w-full mt-1" value={monthlyAmount} onChange={(e) => setMonthlyAmount(e.target.value)} />
          </label>
          <label className="block text-xs text-white/60">
            Monto anual (MXN)
            <input type="number" min="0" step="0.01" className="input-dark w-full mt-1" value={annualAmount} onChange={(e) => setAnnualAmount(e.target.value)} />
          </label>
        </div>
        <button type="button" onClick={activateSubscription} disabled={saving === "activate"} className="btn-primary text-sm">
          {saving === "activate" ? <Loader2 className="animate-spin" size={14} /> : null}
          Suscripción comenzada
        </button>
      </section>

      <section className="glass p-5 rounded-sm space-y-4">
        <div className="flex items-center gap-2 text-white">
          <CreditCard size={18} className="text-[#FFD700]" />
          <h2 className="font-display text-xl">Mercado Pago (DanySolutions)</h2>
        </div>
        <label className="block text-xs text-white/60">
          Public key
          <input className="input-dark w-full mt-1 font-mono text-xs" value={mpPublicKey} onChange={(e) => setMpPublicKey(e.target.value)} />
        </label>
        <label className="block text-xs text-white/60">
          Access token
          <input type="password" className="input-dark w-full mt-1 font-mono text-xs" value={mpAccessToken} onChange={(e) => setMpAccessToken(e.target.value)} />
        </label>
        <div className="flex items-center gap-2 text-white pt-2">
          <Landmark size={18} className="text-[#FFD700]" />
          <h3 className="font-display text-lg">Transferencia bancaria</h3>
        </div>
        <textarea
          className="input-dark w-full min-h-[120px] text-sm"
          placeholder="Banco, CLABE, titular, referencia…"
          value={transferInstructions}
          onChange={(e) => setTransferInstructions(e.target.value)}
        />
        <label className="block text-xs text-white/60">
          Recordatorio N días antes
          <input
            type="number"
            min={1}
            max={30}
            className="input-dark w-full mt-1 max-w-[140px]"
            value={warningDays}
            onChange={(e) => setWarningDays(e.target.value)}
          />
          <span className="block text-white/40 mt-1">
            Días previos al vencimiento para mostrar el banner de aviso en el panel admin (1–30).
          </span>
        </label>
        <button type="button" onClick={saveBillingConfig} disabled={saving === "billing"} className="btn-primary text-sm">
          {saving === "billing" ? <Loader2 className="animate-spin" size={14} /> : null}
          Guardar configuración de cobro
        </button>
      </section>

      <section className="glass p-5 rounded-sm space-y-4">
        <h2 className="font-display text-xl text-white">Historial de pagos</h2>
        {payments.length === 0 ? (
          <p className="text-sm text-white/50">Sin pagos registrados.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="text-white/40 text-xs border-b border-white/10">
                  <th className="py-2 pr-3">Fecha</th>
                  <th className="py-2 pr-3">Monto</th>
                  <th className="py-2 pr-3">Método</th>
                  <th className="py-2 pr-3">Estado</th>
                  <th className="py-2">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-b border-white/5 text-white/80">
                    <td className="py-2 pr-3 text-xs">{(p.created_at || "").slice(0, 10)}</td>
                    <td className="py-2 pr-3">{fmtCurrency(p.amount)}</td>
                    <td className="py-2 pr-3 capitalize">{p.method === "mercadopago" ? "Mercado Pago" : "Transferencia"}</td>
                    <td className="py-2 pr-3">{STATUS_LABELS[p.status] || p.status}</td>
                    <td className="py-2">
                      {p.method === "transfer" && p.status === "pending" && (
                        <div className="flex gap-2">
                          <button type="button" onClick={() => confirmPayment(p.id)} className="text-green-400 hover:text-green-300" title="Confirmar">
                            <Check size={16} />
                          </button>
                          <button type="button" onClick={() => rejectPayment(p.id)} className="text-red-400 hover:text-red-300" title="Rechazar">
                            <X size={16} />
                          </button>
                        </div>
                      )}
                      {p.proof_note && (
                        <div className="text-[10px] text-white/40 mt-1 max-w-[200px] truncate" title={p.proof_note}>
                          {p.proof_note}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
